import { CommandMan, DuctType, type Fan } from "@eisox/api/core-api";
import { ActionButton, Modal } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { FanIcon, PencilIcon } from "@eisox/icons";
import { getDirtyValues } from "@eisox/tools";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { Mode } from "~/components/live-history-toggle";
import { m } from "~/paraglide/messages";

import { useUpdateAirHandlerUnit } from "../hooks/use-update-air-handler-unit";
import { useAirHandlerUnitContext } from "../providers/air-handler-unit.provider";
import * as ActuatorDialog from "./actuator-dialog";

const NAME = "FanDialog";

const fanTitle = (type: DuctType): string => {
  if (type === DuctType.SUPPLY) return m.air_handler_unit_fan_supply();
  if (type === DuctType.RETURN) return m.air_handler_unit_fan_return();
  return m.air_handler_unit_fan();
};

interface CommandFormValues {
  id: string;
  command: number | undefined;
  forcing: number | undefined;
}

interface FanDialogProps extends Modal.RootProps {
  buildingId: string;
  airHandlerUnitId: string;
  fan: Fan;
  children?: React.ReactNode;
}

const FanDialog: React.FC<FanDialogProps> = ({
  buildingId,
  airHandlerUnitId,
  fan,
  open: openProp,
  onOpenChange,
  defaultOpen,
  children,
}) => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen ?? false,
  });

  const { mode } = useAirHandlerUnitContext(NAME);
  const disabled = mode === Mode.History;

  const { mutateAsync } = useUpdateAirHandlerUnit(buildingId);

  const defaultValues: CommandFormValues = { id: fan.id, command: fan.command, forcing: fan.forcing };
  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value, formApi }) => {
      if (disabled) return;
      const dirty = getDirtyValues(formApi.options.defaultValues, value, { identity: "id" });
      if (!dirty) return;
      await mutateAsync({ airHandlerUnitId, body: { fans: [dirty] } });
      setOpen(false);
    },
  });

  // The form outlives the modal content, so reopening must restore the actuator's current values.
  const handleOpenChange = (next: boolean) => {
    if (next) form.reset();
    setOpen(next);
  };

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange}>
      {children && <Modal.Trigger asChild>{children}</Modal.Trigger>}
      <Modal.Content>
        <form
          onSubmit={e => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <Modal.Header
              title={fanTitle(fan.type)}
              subtitle={m.air_handler_unit_actuator_dialog_subtitle()}
              icon={<FanIcon />}
            >
              <Modal.Close asChild>
                <ActionButton variant="cancel">{m.cancel()}</ActionButton>
              </Modal.Close>
              {!disabled && <form.SubmitButton label={m.save()} icon={<PencilIcon />} />}
            </Modal.Header>
            <ActuatorDialog.Group label={m.air_handler_unit_fan()}>
              {(fan.command !== undefined || fan.commandMan === CommandMan.MANUAL) && (
                <form.AppField name="command">
                  {commandField => (
                    <ActuatorDialog.Item label={m.control()}>
                      <ActuatorDialog.Command
                        commandMan={fan.commandMan}
                        value={commandField.state.value}
                        onValueChange={value => commandField.handleChange(value)}
                        disabled={disabled}
                      />
                      <form.AppField name="forcing">
                        {forcingField => (
                          <ActuatorDialog.Forcing
                            command={commandField.state.value}
                            commandMan={fan.commandMan}
                            commandType={fan.commandType}
                            value={forcingField.state.value}
                            onValueChange={value => forcingField.handleChange(value)}
                            disabled={disabled}
                          />
                        )}
                      </form.AppField>
                    </ActuatorDialog.Item>
                  )}
                </form.AppField>
              )}
              <ActuatorDialog.Item label={m.state()}>
                <ActuatorDialog.ActuatorState
                  commandType={fan.commandType}
                  state={fan.state}
                  commandAutomate={fan.commandAutomate}
                  commandMan={fan.commandMan}
                  defect={!!fan.defect}
                  missmatchDefect={!!fan.missmatchDefect}
                />
              </ActuatorDialog.Item>
              {fan.operationHours !== undefined && (
                <ActuatorDialog.Item label={m.air_handler_unit_actuator_operating_hours()}>
                  <ActuatorDialog.State value={`${fan.operationHours}h`} />
                </ActuatorDialog.Item>
              )}
            </ActuatorDialog.Group>
          </form.AppForm>
        </form>
      </Modal.Content>
    </Modal.Root>
  );
};

FanDialog.displayName = NAME;

export { FanDialog };
export type { FanDialogProps };
